import { getCountryContextForDocGeneration } from "@/shared/config/country-legal-context";
import Groq from "groq-sdk";

let groqInstance: Groq | null = null;

function stripGeneratedNotes(content: string): string {
  return content
    .replace(
      /\n*\s*(NOTA|NOTE)\s*:\s*Este documento ha sido generado con asistencia de inteligencia artificial\.[\s\S]*$/i,
      "",
    )
    .trim();
}

function getErrorStatus(error: unknown): number | undefined {
  if (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    typeof (error as { status?: unknown }).status === "number"
  ) {
    return (error as { status: number }).status;
  }
  return undefined;
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message?: unknown }).message === "string"
  ) {
    return (error as { message: string }).message;
  }
  return "Unknown error";
}

export const initGroq = (apiKey: string) => {
  if (!apiKey) {
    throw new Error("Missing Groq API key");
  }

  groqInstance = new Groq({
    apiKey,
    dangerouslyAllowBrowser: true, // Needed for client-side usage
  });

  return groqInstance;
};

export const getGroq = (): Groq => {
  if (!groqInstance) {
    throw new Error("Groq not initialized. Call initGroq first.");
  }
  return groqInstance;
};

// Default model for simulations - fast and free
export const GROQ_MODEL = "llama-3.1-8b-instant";

// Available models - Use DOCUMENTS for ALL legal-related tasks (zero tolerance for errors)
export const GROQ_MODELS = {
  FAST: "llama-3.1-8b-instant", // Fastest - NOT for legal; use for chat/simulations only
  BALANCED: "llama-3.3-70b-versatile", // More capable, still fast
  SMART: "llama-3.3-70b-versatile", // Best quality
  DOCUMENTS: "llama-3.3-70b-versatile", // STANDARD for legal: documents, review, analysis (131K context)
} as const;

export interface SimulationMessage {
  role: "system" | "user" | "assistant";
  content: string;
}

export interface GenerateResponseOptions {
  messages: SimulationMessage[];
  temperature?: number;
  maxTokens?: number;
  stream?: boolean;
}

export interface EvaluationCriteria {
  name: string;
  weight: number; // 0-1, should sum to 1.0
  description: string;
}

export interface EvaluationResult {
  overallScore: number; // 0-100
  criteriaScores: Record<string, number>; // Each criteria score 0-100
  feedback: string;
}

/**
 * Generate AI response for simulation character
 */
export async function generateResponse(
  options: GenerateResponseOptions
): Promise<string> {
  const groq = getGroq();

  try {
    const completion = await groq.chat.completions.create({
      model: GROQ_MODEL,
      messages: options.messages,
      temperature: options.temperature ?? 0.7,
      max_tokens: options.maxTokens ?? 500,
      stream: false,
    });

    return completion.choices[0]?.message?.content || "";
  } catch (error: unknown) {
    // Handle rate limits gracefully
    if (getErrorStatus(error) === 429) {
      throw new Error("API rate limit reached. Please try again in a moment.");
    }

    console.error("Error generating response:", error);
    throw new Error("Failed to generate response. Please try again.");
  }
}

/**
 * Generate AI response with streaming support
 */
export async function* generateResponseStream(
  options: GenerateResponseOptions
): AsyncGenerator<string, void, unknown> {
  const groq = getGroq();

  try {
    const stream = await groq.chat.completions.create({
      model: GROQ_MODEL,
      messages: options.messages,
      temperature: options.temperature ?? 0.7,
      max_tokens: options.maxTokens ?? 500,
      stream: true,
    });

    for await (const chunk of stream) {
      const content = chunk.choices[0]?.delta?.content;
      if (content) {
        yield content;
      }
    }
  } catch (error: unknown) {
    if (getErrorStatus(error) === 429) {
      throw new Error("API rate limit reached. Please try again in a moment.");
    }

    console.error("Error generating streaming response:", error);
    throw new Error("Failed to generate response. Please try again.");
  }
}

/**
 * Evaluate student response based on criteria
 */
export async function evaluateResponse(
  studentResponse: string,
  context: string,
  criteria: EvaluationCriteria[]
): Promise<EvaluationResult> {
  const groq = getGroq();

  const criteriaText = criteria
    .map(
      (c) => `- ${c.name} (${Math.round(c.weight * 100)}%): ${c.description}`
    )
    .join("\n");

  const evaluationPrompt = `You are an expert legal education evaluator. Evaluate the following student response in a legal simulation.

Context: ${context}

Student Response: "${studentResponse}"

Evaluation Criteria:
${criteriaText}

Provide a JSON response with:
1. An overall score from 0-100
2. Individual scores for each criterion (0-100)
3. Brief constructive feedback (2-3 sentences)

Response format:
{
  "overallScore": <number>,
  "criteriaScores": {
    "${criteria[0]?.name}": <number>,
    ...
  },
  "feedback": "<string>"
}`;

  try {
    const completion = await groq.chat.completions.create({
      model: GROQ_MODEL,
      messages: [
        {
          role: "system",
          content:
            "You are a legal education expert. Respond only with valid JSON.",
        },
        {
          role: "user",
          content: evaluationPrompt,
        },
      ],
      temperature: 0.2, // Lower temperature for consistent evaluation
      max_tokens: 500,
      response_format: { type: "json_object" },
    });

    const result = JSON.parse(completion.choices[0]?.message?.content || "{}");

    return {
      overallScore: result.overallScore || 0,
      criteriaScores: result.criteriaScores || {},
      feedback: result.feedback || "No feedback provided.",
    };
  } catch (error) {
    console.error("Error evaluating response:", error);

    // Fallback: Simple length-based evaluation
    const wordCount = studentResponse.trim().split(/\s+/).length;
    const baseScore = Math.min(wordCount * 2, 70); // Up to 70 points for length

    const criteriaScores: Record<string, number> = {};
    criteria.forEach((c) => {
      criteriaScores[c.name] = baseScore;
    });

    return {
      overallScore: baseScore,
      criteriaScores,
      feedback: "Evaluation completed. Continue engaging with the simulation.",
    };
  }
}

// =====================================================
// LEGAL DOCUMENT GENERATION
// =====================================================

export interface LegalDocumentParams {
  documentType: string;
  country: string;
  parties: {
    party1?: string;
    party2?: string;
    [key: string]: unknown;
  };
  details: Record<string, unknown>;
}

/**
 * Generate legal document with Groq
 * Optimized for legal documents with proper formatting
 */
export async function generateLegalDocumentWithGroq(
  params: LegalDocumentParams
): Promise<string> {
  const groq = getGroq();
  const { documentType, country, parties, details } = params;

  const countryContext = getCountryContextForDocGeneration(country);

  const systemPrompt = `Eres un experto abogado especializado en derecho de ${countryContext}.

Tu tarea es redactar documentos legales profesionales, precisos y completos que cumplan con todas las normativas legales vigentes en ${country} en el año ${new Date().getFullYear()}.

INSTRUCCIONES IMPORTANTES:
1. Usa terminología legal apropiada y formal
2. Incluye todas las cláusulas esenciales para el tipo de documento
3. Asegúrate de que el documento sea legalmente vinculante
4. Usa el formato estándar para este tipo de documento en ${country}
5. Incluye espacios para firmas y datos faltantes si es necesario
6. Sé específico y detallado en las obligaciones y derechos
7. Incluye cláusulas de resolución de conflictos apropiadas
8. NO uses placeholders genéricos como [NOMBRE], usa los datos proporcionados
9. Si falta información crítica, menciona "A COMPLETAR: [descripción]"
10. El documento debe estar listo para ser firmado
11. NO inventes leyes, artículos o referencias legales que no existan. Solo usa leyes reales y verificadas
12. Si no estás seguro de una ley específica, menciona las leyes generales aplicables sin inventar detalles

FORMATO:
- Título centrado en mayúsculas
- Párrafos numerados o con letras según corresponda
- Secciones claramente delimitadas
- Espacios para firmas al final
- Fecha y lugar

FORMATO FINAL OBLIGATORIO:
- Devuelve ÚNICAMENTE el documento legal final.
- No agregues notas, disclaimers, advertencias extra ni texto fuera del documento.`;

  const userPrompt = `Genera un ${documentType} para ${country}.

DATOS PROPORCIONADOS:
${JSON.stringify({ parties, details }, null, 2)}

Redacta el documento completo, profesional y listo para usar.`;

  try {
    const completion = await groq.chat.completions.create({
      model: GROQ_MODELS.DOCUMENTS, // Use Mixtral for best quality
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userPrompt },
      ],
      temperature: 0.2, // Lower temperature for consistent legal text
      max_tokens: 4000,
      top_p: 0.95,
    });

    const content = completion.choices[0]?.message?.content;

    if (!content) {
      throw new Error("No content generated by Groq");
    }

    return stripGeneratedNotes(content);
  } catch (error: unknown) {
    console.error("Groq legal document generation error:", error);
    throw new Error(`Error generating legal document: ${getErrorMessage(error)}`);
  }
}

/**
 * Generate a simple legal document with Groq (faster, less structured)
 */
export async function generateSimpleDocumentWithGroq(
  documentType: string,
  country: string,
  userInput: string
): Promise<string> {
  const groq = getGroq();

  const countryContext = getCountryContextForDocGeneration(country).split(",")[0];

  const systemPrompt = `Eres un abogado experto en ${countryContext}. Redacta documentos legales profesionales y precisos.`;

  const userPrompt = `Genera un ${documentType} para ${countryContext}.

Información: ${userInput}

Redacta el documento completo, profesional y listo para usar.`;

  try {
    const completion = await groq.chat.completions.create({
      model: GROQ_MODELS.DOCUMENTS,
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userPrompt },
      ],
      temperature: 0.2,
      max_tokens: 3000,
    });

    const content = completion.choices[0]?.message?.content;

    if (!content) {
      throw new Error("No content generated by Groq");
    }

    return stripGeneratedNotes(content);
  } catch (error: unknown) {
    console.error("Groq simple document generation error:", error);
    throw new Error(`Error generating document: ${getErrorMessage(error)}`);
  }
}

/**
 * Review/improve an existing legal document with Groq
 */
/**
 * Improve a legal document with optional user instructions (Groq)
 */
export async function improveDocumentWithGroq(
  documentContent: string,
  country: string,
  instructions?: string
): Promise<string> {
  const groq = getGroq();
  const countryContext = getCountryContextForDocGeneration(country).split(",")[0];

  let systemPrompt = `Eres un abogado experto en ${countryContext} especializado en mejorar documentos legales.

INSTRUCCIONES:
1. Mejora el documento legal proporcionado manteniendo su estructura y propósito original
2. Corrige errores gramaticales y ortográficos
3. Mejora la claridad y precisión del lenguaje jurídico
4. Asegúrate de que el documento cumpla con las leyes de ${countryContext}
5. Devuelve SOLO el documento mejorado, sin comentarios adicionales`;

  if (instructions?.trim()) {
    systemPrompt += `\n\nINSTRUCCIONES ESPECÍFICAS DEL USUARIO:\n${instructions.trim()}`;
  }

  const userPrompt = `DOCUMENTO A MEJORAR:\n\n${documentContent}`;

  const completion = await groq.chat.completions.create({
    model: GROQ_MODELS.DOCUMENTS,
    messages: [
      { role: "system", content: systemPrompt },
      { role: "user", content: userPrompt },
    ],
    temperature: 0.6,
    max_tokens: 4000,
  });

  const improvedContent = completion.choices[0]?.message?.content?.trim();
  if (!improvedContent) {
    throw new Error("No se pudo mejorar el documento");
  }

  return stripGeneratedNotes(improvedContent);
}

export async function reviewDocumentWithGroq(
  documentContent: string,
  country: string,
  improvements?: string
): Promise<{ suggestions: string; improvedDocument: string }> {
  const groq = getGroq();

  const countryContext = getCountryContextForDocGeneration(country).split(",")[0];

  const systemPrompt = `Eres un abogado experto revisor de documentos legales en ${countryContext}.
Analiza documentos legales y proporciona mejoras específicas y precisas.`;

  const userPrompt = `Revisa el siguiente documento legal:

${documentContent}

${improvements ? `Enfócate especialmente en: ${improvements}` : ""}

Proporciona:
1. Una lista de sugerencias específicas de mejora
2. El documento mejorado con los cambios aplicados

Formato tu respuesta así:
SUGERENCIAS:
[lista numerada de sugerencias]

DOCUMENTO MEJORADO:
[documento completo mejorado]`;

  try {
    const completion = await groq.chat.completions.create({
      model: GROQ_MODELS.DOCUMENTS,
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userPrompt },
      ],
      temperature: 0.4,
      max_tokens: 5000,
    });

    const content = completion.choices[0]?.message?.content || "";

    // Parse response
    const [suggestionsSection, ...documentParts] = content.split(
      "DOCUMENTO MEJORADO:"
    );
    const suggestions = suggestionsSection.replace("SUGERENCIAS:", "").trim();
    const improvedDocument = documentParts.join("DOCUMENTO MEJORADO:").trim();

    return {
      suggestions,
      improvedDocument: improvedDocument || documentContent,
    };
  } catch (error: unknown) {
    console.error("Groq document review error:", error);
    throw new Error(`Error reviewing document: ${getErrorMessage(error)}`);
  }
}

// =====================================================
// LAWYER BIO GENERATION
// =====================================================

export interface LawyerBioParams {
  barNumber?: string;
  university?: string;
  yearsOfExperience?: number;
  practiceAreas?: string[];
  hourlyRate?: string;
  acceptCases?: boolean;
  acceptClients?: boolean;
  currentBio?: string;
  language?: "en" | "es";
}

/**
 * Generate an attractive professional bio for a lawyer based on their profile data
 */
export async function generateLawyerBioWithGroq(
  params: LawyerBioParams
): Promise<string> {
  const groq = getGroq();
  const {
    university,
    yearsOfExperience = 0,
    practiceAreas = [],
    acceptCases,
    acceptClients,
    currentBio,
    language = "es",
  } = params;

  const langInstruction =
    language === "es"
      ? "Responde únicamente en español."
      : "Respond only in English.";

  const practiceAreasText =
    practiceAreas.length > 0
      ? practiceAreas.join(", ")
      : language === "es"
        ? "derecho general"
        : "general law";

  const systemPrompt = `You are an expert copywriter specializing in professional lawyer profiles for legal directories and firm websites.

Your task is to create a compelling, coherent biography (2-4 short paragraphs) that:
1. Highlights professional trajectory, experience, and specialization in a narrative flow
2. Emphasizes practice areas and how the lawyer serves clients
3. Uses a professional but approachable tone
4. Is suitable for a public profile that attracts and builds trust with clients
5. Feels authentic—avoid generic stock phrases

CRITICAL - DO NOT INCLUDE in the bio:
- Exequátur, bar number, license number, or any professional registration ID
- Administrative or internal credentials
- Specific hourly rates or prices (you may convey "competitive" or similar if relevant)
- Literal data dumps; weave information naturally into the narrative

Use the provided data only as context to craft a coherent narrative. The bio must read as a professional introduction, not a form.

${langInstruction}
Output ONLY the bio text. No titles, no bullet points, no markdown, no headers.`;

  const contextParts: string[] = [
    `- Alma mater: ${university || "Not provided"}`,
    `- Years of professional experience: ${yearsOfExperience}`,
    `- Practice areas: ${practiceAreasText}`,
  ];
  if (acceptCases || acceptClients) {
    contextParts.push(
      `- Currently ${acceptCases && acceptClients ? "accepting new cases and direct clients" : acceptCases ? "accepting new cases" : "available for direct client consultations"}`
    );
  }
  if (currentBio?.trim()) {
    contextParts.push(`\nCurrent draft (use as inspiration; improve flow and coherence):\n${currentBio.trim()}`);
  }

  const userPrompt = `Create a professional lawyer bio. Use this as context only—do not list or repeat it literally:

${contextParts.join("\n")}

Generate a coherent, engaging bio that reads naturally. Never mention exequátur, bar number, or license numbers.`;

  try {
    const completion = await groq.chat.completions.create({
      model: GROQ_MODELS.BALANCED,
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userPrompt },
      ],
      temperature: 0.5,
      max_tokens: 600,
    });

    const content = completion.choices[0]?.message?.content?.trim();

    if (!content) {
      throw new Error("No content generated");
    }

    return content;
  } catch (error: unknown) {
    if (getErrorStatus(error) === 429) {
      throw new Error("API rate limit reached. Please try again in a moment.");
    }
    console.error("Groq lawyer bio generation error:", error);
    throw new Error(
      getErrorMessage(error) || "Failed to generate bio. Please try again."
    );
  }
}

// =====================================================
// EXAM TRANSLATION
// =====================================================

export interface ExamQuestion {
  id: number;
  question: string;
  type: "multiple_choice" | "multiple_select" | "text";
  options?: Array<{ id: number; text: string }>;
  correct_answer: number | string | number[] | string[];
  points: number;
  explanation?: string;
}

export interface ExamData {
  exam_name: string;
  description?: string;
  questions: ExamQuestion[];
}

/**
 * Translate exam content using Groq AI
 * Translates exam name, description, questions, options, and explanations
 */
export async function translateExamWithGroq(
  examData: ExamData,
  targetLanguage: "en" | "es" | "pt"
): Promise<ExamData> {
  const groq = getGroq();

  const languageNames = {
    en: "English",
    es: "Spanish",
    pt: "Portuguese",
  };

  const systemPrompt = `You are an expert translator specializing in legal and educational content. Your task is to translate exam content from Spanish to ${languageNames[targetLanguage]} while maintaining:
1. Legal terminology accuracy
2. Question structure and format
3. Option IDs and order (do not change option IDs)
4. Correct answer references (maintain the same indices/values)
5. Professional and clear language

IMPORTANT:
- Keep all JSON structure intact
- Do not change option IDs (they must remain the same)
- Do not change correct_answer values (they reference option IDs)
- Translate all text content: exam name, description, questions, options, explanations
- Maintain the same format and structure
- Use proper legal terminology in ${languageNames[targetLanguage]}`;

  const examJson = JSON.stringify(examData, null, 2);

  const userPrompt = `Translate the following exam from Spanish to ${languageNames[targetLanguage]}. Maintain the exact JSON structure and all IDs/indices:

${examJson}

Return ONLY the translated JSON object with the same structure. Do not include any explanations or markdown formatting.`;

  try {
    const completion = await groq.chat.completions.create({
      model: GROQ_MODELS.BALANCED, // Use balanced model for good quality and speed
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userPrompt },
      ],
      temperature: 0.3, // Lower temperature for consistent translation
      max_tokens: 8000, // Large context for full exams
      response_format: { type: "json_object" },
    });

    const translatedContent = completion.choices[0]?.message?.content;

    if (!translatedContent) {
      throw new Error("No translation content generated by Groq");
    }

    // Parse the translated JSON
    let translatedExam: ExamData;
    try {
      // Remove any markdown code blocks if present
      const cleanedContent = translatedContent
        .replace(/```json\n?/g, "")
        .replace(/```\n?/g, "")
        .trim();
      translatedExam = JSON.parse(cleanedContent);
    } catch (parseError) {
      console.error("Error parsing translated exam:", parseError);
      throw new Error("Failed to parse translated exam. Please try again.");
    }

    // Validate structure
    if (!translatedExam.exam_name || !translatedExam.questions) {
      throw new Error("Translated exam missing required fields");
    }

    // Ensure all questions have the correct structure
    translatedExam.questions = translatedExam.questions.map((q, idx) => ({
      ...q,
      id: examData.questions[idx]?.id ?? q.id,
      type: examData.questions[idx]?.type ?? q.type,
      points: examData.questions[idx]?.points ?? q.points,
      correct_answer:
        examData.questions[idx]?.correct_answer ?? q.correct_answer, // Keep original answer indices
    }));

    return translatedExam;
  } catch (error: unknown) {
    console.error("Groq exam translation error:", error);

    // Handle rate limits
    if (getErrorStatus(error) === 429) {
      throw new Error(
        "Translation rate limit reached. Please try again in a moment."
      );
    }

    // Handle parsing errors
    const errorMessage = getErrorMessage(error);
    if (errorMessage.includes("parse") || errorMessage.includes("JSON")) {
      throw new Error("Error processing translation. Please try again.");
    }

    throw new Error(
      `Error translating exam: ${errorMessage || "Unknown error"}`
    );
  }
}
